import React from "react";
import { Check } from "lucide-react";
import PhoneMock, { TrackTimeScreen, OverviewScreen, MoneyScreen } from "../components/PhoneMock";
import { BG_SOFT, BRAND, TEXT_INK, TEXT_DARK, CONTAINER } from "../tokens";

interface Block {
  eyebrow: string;
  title: string;
  copy: string;
  checklist: string[];
  screen: React.ReactNode;
  bar: "time" | "pay";
  tab: string;
  reverse: boolean;
}

export default function DayInLife() {
  const blocks: Block[] = [
    { eyebrow: "For your team", title: "Their whole work life, one tap away", copy: "Tap clock in. Tap clock out. The worked time becomes hours that drop straight onto the timesheet. If they leave while still clocked in, Novala reminds them so the numbers stay right.", checklist: ["Clock in and out (hours fill on a correct clock out)", "Reminder to clock out if they leave the work location", "Pay stubs and tax forms free to them"], screen: <TrackTimeScreen />, bar: "time", tab: "Track Time", reverse: false },
    { eyebrow: "For your team", title: "Today at a glance", copy: "An overview screen pulls the day, the latest pay, and the next shift into one view. No app hunting for the basics.", checklist: ["Day total and clock in status", "Latest pay and upcoming shift", "Time off balance"], screen: <OverviewScreen />, bar: "time", tab: "Overview", reverse: true },
    { eyebrow: "For you, the owner", title: "Approve once, pay with confidence", copy: "You see the same Money, Time Off, Taxes, Settings, Help tabs the team uses, with full owner detail behind every number. Approve, file, done.", checklist: ["Net pay, taxes, deductions at a glance", "Year to date and latest pay", "Year end tax documents"], screen: <MoneyScreen />, bar: "pay", tab: "Money", reverse: false },
  ];
  return (
    <section style={{ background: BG_SOFT, padding: "100px 0" }}>
      <div style={CONTAINER}>
        <div style={{ textAlign: "center", maxWidth: 720, margin: "0 auto 60px" }}>
          <span style={{ display: "inline-block", fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: BRAND, marginBottom: 12 }}>A day in the life</span>
          <h2 style={{ fontSize: "clamp(32px, 4vw, 42px)", fontWeight: 800, color: TEXT_INK, letterSpacing: "-0.02em", lineHeight: 1.1, margin: 0 }}>
            What it looks like, day to day
          </h2>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 80 }}>
          {blocks.map((b, i) => (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 60, alignItems: "center" }}>
              <div style={{ order: b.reverse ? 2 : 1 }}>
                <span style={{ display: "inline-block", fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: BRAND, marginBottom: 10 }}>{b.eyebrow}</span>
                <h3 style={{ fontSize: 30, fontWeight: 800, color: TEXT_INK, letterSpacing: "-0.02em", lineHeight: 1.15, margin: "0 0 16px" }}>{b.title}</h3>
                <p style={{ fontSize: 15.5, color: TEXT_DARK, lineHeight: 1.6, margin: "0 0 22px" }}>{b.copy}</p>
                <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 10 }}>
                  {b.checklist.map((c, j) => (
                    <li key={j} style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 14, color: TEXT_INK }}>
                      <span style={{ width: 20, height: 20, borderRadius: "50%", background: "rgba(15, 149, 153, 0.10)", display: "grid", placeItems: "center", flexShrink: 0, marginTop: 1 }}>
                        <Check size={11} color={BRAND} />
                      </span>
                      {c}
                    </li>
                  ))}
                </ul>
              </div>
              <div style={{ order: b.reverse ? 1 : 2, display: "grid", placeItems: "center" }}>
                <div style={{ position: "relative", width: 360, height: 600, display: "grid", placeItems: "center" }}>
                  <div style={{ position: "absolute", width: 380, height: 380, borderRadius: "50%", background: "rgba(15, 149, 153, 0.06)" }} />
                  <div style={{ position: "relative" }}>
                    <PhoneMock screen={b.screen} activeTab={b.tab} barVariant={b.bar} />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}